/**
 * Application router (VI-513).
 *
 * Code-based TanStack Router tree — same ecosystem as the portal's existing
 * `@tanstack/react-query`. Replaces the former `view`/`listKind`/`selected*`
 * state-toggle in App.tsx, so each view now has a real URL: the browser Back
 * button navigates within the app and every view deep-links.
 *
 * Routes (all direct children of the root layout — no nested outlets; each is a
 * standalone page rendered into the root `<Outlet />`):
 *   /                      → redirect to /catalog
 *   /catalog               → VendorListView   (Company Catalog)
 *   /catalog/$candidateId  → VendorDetailView  (keyed on the numeric candidate id)
 *   /candidates            → CandidateListView (Candidate Catalog)
 *   /candidates/$id        → CandidateDetailView
 *   /orphaned              → OrphanedCandidatesView (non-curated, zero-tenant records)
 *   /troubleshooting       → TroubleshootingView (ad-hoc VGS directory lookups)
 *
 * The `$candidateId` / `$id` segments are parsed to `number` at the route so
 * every consumer (`useParams`, `navigate({ params })`) is typed numerically —
 * matching the API fetchers and react-query keys, which key on the numeric id.
 */
import {
    createRootRoute,
    createRoute,
    createRouter,
    notFound,
    redirect,
} from '@tanstack/react-router';
import { App } from './App';
import { NotFoundView } from './components/NotFoundView';
import { VendorListView } from './views/VendorListView';
import { VendorDetailView } from './views/VendorDetailView';
import { CandidateListView } from './views/CandidateListView';
import { CandidateDetailView } from './views/CandidateDetailView';
import { OrphanedCandidatesView } from './views/OrphanedCandidatesView';
import { TroubleshootingView } from './views/TroubleshootingView';
import { AdminUsersView } from './views/AdminUsersView';

const rootRoute = createRootRoute({ component: App });

const indexRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    beforeLoad: () => {
        throw redirect({ to: '/catalog' });
    },
});

const catalogRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/catalog',
    component: VendorListView,
});

const catalogDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/catalog/$candidateId',
    params: {
        parse: (raw) => ({ candidateId: Number(raw.candidateId) }),
        stringify: (params) => ({ candidateId: String(params.candidateId) }),
    },
    // Reject a bad deep link (e.g. /catalog/abc → NaN) at the route boundary so
    // NaN never reaches the query key or API URL; a clean not-found renders
    // instead of a confusing error state.
    beforeLoad: ({ params }) => {
        if (!Number.isInteger(params.candidateId) || params.candidateId < 1) {
            throw notFound();
        }
    },
    component: VendorDetailView,
});

const candidatesRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/candidates',
    component: CandidateListView,
});

const candidatesDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/candidates/$id',
    params: {
        parse: (raw) => ({ id: Number(raw.id) }),
        stringify: (params) => ({ id: String(params.id) }),
    },
    // See the catalog detail route: reject bad ids at the route boundary.
    beforeLoad: ({ params }) => {
        if (!Number.isInteger(params.id) || params.id < 1) {
            throw notFound();
        }
    },
    component: CandidateDetailView,
});

const orphanedRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/orphaned',
    component: OrphanedCandidatesView,
});

const troubleshootingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/troubleshooting',
    component: TroubleshootingView,
});

const adminsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/admins',
    component: AdminUsersView,
});

const routeTree = rootRoute.addChildren([
    indexRoute,
    catalogRoute,
    catalogDetailRoute,
    candidatesRoute,
    candidatesDetailRoute,
    orphanedRoute,
    troubleshootingRoute,
    adminsRoute,
]);

export const router = createRouter({ routeTree, defaultNotFoundComponent: NotFoundView });

declare module '@tanstack/react-router' {
    interface Register {
        router: typeof router;
    }
}
